import { QUESTIONS, PILLARS, TIERS } from '../constants';
import { AssessmentContent } from './types';

/**
 * Tier result summaries, ordered lowest → highest to match `TIERS`.
 * (Previously inlined in `utils.ts#getTierSummary`.)
 */
export const DEFAULT_TIER_SUMMARIES: string[] = [
  "The foundational elements needed for an AI-native product aren't yet in place. The gaps are identifiable and addressable — a structured diagnostic is the right place to start.",
  "Your product is in the early stages of AI readiness. There's awareness and intent, but most pillars need deliberate investment before you can execute on AI capabilities at scale.",
  'Your product shows real momentum. The foundations are there — but intelligence is likely still additive rather than core. The next phase is architectural: making AI a first-class citizen of how your product behaves.',
  'Your product is well ahead of the curve across most pillars. The remaining gaps are addressable — formalizing your AI roadmap and closing infrastructure blind spots will get you to AI-native.',
  'Your product is operating at the frontier. Intelligence is woven into the core — not bolted on. Focus now on governance, model observability at scale, and maintaining the velocity that got you here.',
];

/**
 * Per-pillar insight copy, ordered to match `PILLAR_LABELS`.
 * (Previously inlined in `utils.ts#INSIGHT_COPY`.)
 */
export const DEFAULT_INSIGHTS: Array<{ strong: string; weak: string }> = [
  {
    strong:
      'Clear AI direction gives your team focus and alignment — a competitive advantage when translating vision into shipped products.',
    weak: 'Without a documented AI strategy, teams move in different directions. Formalizing your roadmap is the highest-leverage first step.',
  },
  {
    strong:
      'A modular, API-first architecture dramatically reduces the friction of integrating AI services and iterating on them.',
    weak: 'This is where self-assessment and external diagnostic findings diverge most. Organizations frequently overestimate infrastructure readiness without an evidence-based review.',
  },
  {
    strong:
      'Clean, accessible data is the fuel for AI. Your readiness here gives you a meaningful head start over peers still fighting data silos.',
    weak: 'Data quality and accessibility are the most common blockers to AI deployment. Addressing this unlocks progress across all other pillars.',
  },
  {
    strong:
      "Your team's delivery cadence and experimentation culture are well above peer average — a strong foundation for rapid AI integration once infrastructure catches up.",
    weak: 'Shipping cadence directly impacts your ability to iterate on AI features. Improving deployment frequency should be a near-term focus.',
  },
  {
    strong:
      'A product team that can adapt AI behavior post-launch without full engineering cycles has a meaningful competitive edge — you can iterate on intelligence, not just features.',
    weak: 'Product adaptability is often the hidden bottleneck. When AI behavior is locked behind full release cycles, teams lose the ability to respond to model drift, user feedback, and changing conditions.',
  },
];

/**
 * The built-in copy. With no Sanity document (or an empty one) the resolved
 * content equals this, so the page renders exactly as it did before the CMS
 * migration. Question/option/tier/pillar text is derived from the code
 * constants so the defaults can never drift out of sync with the logic.
 */
export const DEFAULT_CONTENT: AssessmentContent = {
  metaTitle: 'AI-Native Product Assessment — Foxbox Digital',
  metaDescription:
    'Answer 8 questions and receive a personalized AI-native product readiness score across five dimensions — instantly.',

  navPrimary: 'AI-NATIVE',
  navSecondary: 'Product Diagnostic',
  footerCopyright: 'Foxbox Digital. All rights reserved.',

  sidePanel: {
    headlineLead: 'All roads lead to',
    headlineHighlight: 'AI-native.',
    paragraphs: [
      'Beyond AI-features and bolted-on chatbots, AI-native products put intelligence at the core of how they behave, adapt, and evolve. They are systems designed to learn from every interaction, adapt continuously, and make decisions probabilistically.',
      'AI-native is the inevitable future state of virtually any digital product.',
      'We created this diagnostic to help CTOs and product leaders understand where they stand today and what it will take to compete tomorrow.',
    ],
    youllGetTitle: "You'll get",
    youllGet: [
      'Clear benchmarks across 5 dimensions',
      'An objective view of your existing systems and where they fall short',
      'A renewed perspective on what to build next and where to begin',
    ],
    timeBadge: 'Under 3 minutes  ·  Free  ·  Instant results',
  },

  intro: {
    eyebrow: 'Self-Assessment',
    title: 'Start with clarity',
    subtitle: '8 short questions, across 5 dimensions for a clear picture of where you stand and what to build next.',
    statQuestionsLabel: 'Questions',
    statDimensionsLabel: 'Dimensions',
    statTimeValue: '3 min',
    statTimeLabel: 'Average',
    ctaLabel: 'Take the self-assessment',
  },

  pillars: [...PILLARS],

  questions: QUESTIONS.map((q) => ({ question: q.q, options: [...q.opts] })),

  tiers: TIERS.map((t) => ({ label: t.label, range: t.range })),

  tierSummaries: DEFAULT_TIER_SUMMARIES,

  insights: DEFAULT_INSIGHTS,

  gate: {
    categoryLabel: 'Your details',
    title: 'One last step',
    subtitle: 'Enter your details to unlock your personalized score.',
    firstNameLabel: 'First name',
    firstNamePlaceholder: 'Jordan',
    lastNameLabel: 'Last name',
    lastNamePlaceholder: 'Lee',
    emailLabel: 'Work email',
    emailPlaceholder: 'jordan@company.com',
    companyLabel: 'Company name',
    companyPlaceholder: 'Acme Corp',
    companySizeLabel: 'Company size',
    roleLabel: 'Your role',
    roleOptions: ['Product leader', 'CTO / VP Eng', 'CEO / Founder', 'Engineering manager'],
    privacyNote: "No spam. Your results are yours — we'll only follow up if you ask us to.",
    submitLabel: 'See my results',
  },

  results: {
    navLabel: 'Your AI Readiness Results',
    eyebrow: "Your Product's AI Native Readiness Score",
    title: "Here's your score",
    outOfLabel: 'out of 100',
    ctaPrimary: 'Get your full diagnostic report →',
    ctaSecondary: 'Book a strategy call',
    spiderTitle: 'Readiness by pillar',
    legendYour: 'Your score',
    legendPeer: 'Peer avg.',
    pillarBreakdownTitle: 'Pillar breakdown',
    tierTitle: 'Readiness tier',
    insightsTitle: 'Key insights',
  },

  confirm: {
    navSecondary: 'Product Assessment',
    title: "You're all set",
    body: 'Your results have been sent to {{email}}. A Foxbox strategist will review your score and follow up within 1 business day — or book a time directly below.',
    tierCaption: 'Your AI Readiness tier',
    meetingEyebrow: 'Book a strategy call',
    meetingTitle: 'Foxbox Digital — AI Readiness Review',
    meetingMeta: '30 min  ·  Remote  ·  Free',
    skipLabel: "Skip — I'll wait for your team to reach out",
  },
};
